/* eslint-disable react/no-danger */

import { Button, Alert, IconLoading } from '@harvesthq/porchlight'
import autosize from 'autosize'
import classNames from 'classnames'
import moment from 'moment'
import { useState, useRef, useEffect } from 'preact/compat'

const browserExtensionServices = [
  'chrome.google.com',
  'microsoft.com/edge',
  'mozilla.org/firefox',
]

const isBrowserExtension = (service) =>
  service && browserExtensionServices.includes(service)

import { HarvestAppApi } from '../lib/api'
import {
  accessToken,
  currentAccountId,
  removeCredentials,
} from '../lib/authentication'
import {
  broadcastMessage,
  broadcastMessageFrameClose,
  BroadcastMessageType,
} from '../lib/broadcastMessage'
import { initialiseClockwork } from '../lib/clockwork'
import gtm from '../lib/gtm'
import useFetchCompany from '../lib/hooks/useFetchCompany'
import useFetchPlatformData from '../lib/hooks/useFetchPlatformData'
import useFetchProjectsAndTasks from '../lib/hooks/useFetchProjectsAndTasks'
import useFetchRunningTimer from '../lib/hooks/useFetchRunningTimer'
import { watchForHeightChanges } from '../lib/util'

import { AsanaProjectSyncPlug } from './AsanaProjectSyncPlug'
import { ChangeDate } from './ChangeDate'
import { CreateProjectLink } from './CreateProjectLink'
import { DurationInput } from './DurationInput'
import { ErrorMessage } from './ErrorMessage'
import { Header } from './Header'
import { NoProjects } from './NoProjects'
import { ProjectSelect } from './ProjectSelect'
import { RunningTimer } from './RunningTimer'
import { TaskSelect } from './TaskSelect'
import { TimerSummary } from './TimerSummary'
import { TimestampInputs } from './TimestampInputs'

const notesValidationMessage = 'Notes are required'

export const TimerForm = ({
  defaultProjectCode,
  defaultProjectName,
  closable,
  chromeless,
  params,
}) => {
  // Fetch Data
  const { company, loading, error } = useFetchCompany()
  const {
    data: platformData,
    error: platformDataError,
    loading: platformDataLoading,
    refetch: platformRefetch,
  } = useFetchPlatformData(params)
  const {
    projects,
    loading: projectsLoading,
    error: projectsError,
  } = useFetchProjectsAndTasks()
  const {
    timeEntry: lastRunningTimer,
    loading: timerLoading,
    error: timerError,
  } = useFetchRunningTimer()

  // State
  const [warning, setWarning] = useState()
  const [submittingForm, setSubmittingForm] = useState(false)
  const [projectId, setProjectId] = useState()
  const [taskId, setTaskId] = useState()
  const [notes, setNotes] = useState(params.external_item_name)
  const [initialisedGTM, setInitialisedGTM] = useState(false)
  const [initialisedClockwork, setInitialisedClockwork] = useState(false)
  const [startedTime, setStartedTime] = useState()
  const [endedTime, setEndedTime] = useState()
  const [duration, setDuration] = useState()
  const [spentDate, setSpentDate] = useState(moment())
  const [runningTimer, setRunningTimer] = useState()
  const [showSaved, setShowSaved] = useState(false)

  // Refs
  const inputFormRef = useRef(null)
  const textareaRef = useRef(null)

  // Effects
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSaved(false)
    }, 2000)
    return () => clearTimeout(timer)
  }, [showSaved])

  useEffect(() => {
    if (textareaRef?.current) {
      textareaRef.current.oninvalid = () =>
        textareaRef.current.setCustomValidity(notesValidationMessage)
      autosize(textareaRef.current)
    }
  })

  const lockedTimesheet = platformData?.timesheetApproved
  const userIsAdmin = platformData?.user?.canReadAllCostRates
  const isValid = projectId && taskId
  const header = !chromeless && (
    <Header
      baseUri={company?.baseUri}
      userCanSwitchAccounts={platformData?.user?.canSwitchAccounts}
    />
  )
  const tasks = projects.find(({ id }) => id === projectId)?.tasks
  const noProjectsHasTasks =
    projects.length > 0 && !projects.some((project) => project.tasks.length > 0)

  if (platformData?.dataLayer && !initialisedGTM) {
    setInitialisedGTM(
      gtm({
        ...platformData.dataLayer,
        'Clean Path': '/platform/timer',
      })
    )
  }

  if (company && !initialisedClockwork) {
    setInitialisedClockwork(initialiseClockwork(company))
  }

  if (
    !platformData &&
    (loading || projectsLoading || timerLoading || platformDataLoading)
  ) {
    return (
      <div
        className="pds-flex pds-items-center pds-justify-center"
        style={{ height: '300px' }}
      >
        <IconLoading height={32} width={32} />
      </div>
    )
  }

  if (error || timerError || platformDataError || projectsError || !company) {
    const errorObj = error || timerError || platformDataError || projectsError

    setInterval(watchForHeightChanges, 200)

    if (errorObj?.status === 401) {
      removeCredentials()
      window.location.reload()
    }

    return (
      <>
        {header}
        <ErrorMessage
          error={errorObj}
          accountId={currentAccountId || undefined}
        />
      </>
    )
  }

  if (
    !runningTimer && // If there's no running timer
    lastRunningTimer && // And we have fetched a lastRunningTimer
    (isBrowserExtension(params.service) || // And the platform is opened in the browser extension
      (lastRunningTimer.external_reference && // Or we have and external reference that's matching the running timer
        lastRunningTimer.external_reference?.id === params.external_item_id &&
        lastRunningTimer.external_reference?.group_id == // Use == because the params.external_group_id can be undefined when empty whereas the lastRunningTimer.external_reference.group_id is null when empty
          params.external_group_id))
  ) {
    setRunningTimer(lastRunningTimer) // Set the lastRunningTimer
  }

  const onSubmit = async (e) => {
    e.preventDefault()

    if (!projectId || !taskId || !accessToken || !currentAccountId) return

    setSubmittingForm(true)

    const external_reference = {
      id: params.external_item_id,
      group_id: params.external_group_id,
      account_id: params.external_account_id,
      permalink: params.permalink,
      service: params.service,
      app_name: params.app_name,
    }

    const timeEntry = {
      project_id: projectId,
      task_id: taskId,
      spent_date: spentDate.format('YYYY-MM-DD'),
      started_time: startedTime,
      ended_time: endedTime,
      notes,
      external_reference,
    }

    if (notes) {
      timeEntry.notes = notes
    }

    if (company.wantsTimestampTimers) {
      Object.assign(timeEntry, {
        started_time: startedTime,
        ended_time: endedTime,
      })
    } else {
      timeEntry.hours = duration
    }

    const client = new HarvestAppApi(accessToken, currentAccountId)

    const response = await client
      .post('/time_entries', timeEntry)
      .catch((error) => {
        const errorMessage = error.message || error.error || error
        return { message: errorMessage }
      })

    if (response?.message) {
      setWarning(response.message)
    } else if (response?.is_running) {
      setRunningTimer(response)

      broadcastMessage({
        type: BroadcastMessageType.TimerStarted,
        value: {
          external_reference,
        },
        crossOrigin: true,
      })

      clearTime()
      broadcastMessageFrameClose()
    } else {
      setShowSaved(true)
      clearTime()
      onStopTimer({ external_reference })
    }
    setSubmittingForm(false)
  }

  const onKeyDown = (e) => {
    if (e.key == 'Enter' && e.shiftKey == false) {
      return onSubmit(e)
    }

    if (textareaRef?.current) {
      autosize(textareaRef.current)
    }
  }

  const clearTime = () => {
    if (company.wantsTimestampTimers) {
      setStartedTime(undefined)
      setEndedTime(undefined)
    } else {
      setDuration(undefined)
    }
    inputFormRef.current?.clear()
  }

  const onStopTimer = ({ external_reference }) => {
    broadcastMessage({
      type: BroadcastMessageType.TimerStopped,
      value: { external_reference },
      crossOrigin: true,
    })

    setRunningTimer(undefined)
    platformRefetch()
    broadcastMessageFrameClose()
  }

  const formClassName = classNames({ 'pds-p-md': !chromeless })

  setTimeout(watchForHeightChanges, 200)

  const timerSummary = company && platformData && (
    <TimerSummary
      baseUri={company.baseUri}
      userCanSwitchAccounts={platformData.user.canSwitchAccounts}
      totalHours={platformData.externalReference.totalHours}
      iconUrl={platformData.externalReference.iconUrl}
      externalItemName={params.external_item_name}
      serviceName={params.service}
      chromeless={chromeless}
    />
  )

  if (runningTimer) {
    return (
      <div className="pds-row pds-m-0">
        <div className="pds-column pds-p-0">
          {header}
          <div className={formClassName}>
            <RunningTimer
              timer={runningTimer}
              onStopTimer={onStopTimer}
              wantsTimestampTimers={company.wantsTimestampTimers}
              closable={closable}
            />
            {timerSummary}
          </div>
        </div>
      </div>
    )
  }

  if (noProjectsHasTasks || !projects.length) {
    return (
      <NoProjects
        externalGroupName={params.external_group_name}
        baseUri={company.baseUri}
        userCanSwitchAccounts={platformData?.user.canSwitchAccounts || false}
        userCanCreateProjects={platformData?.user.canCreateProjects || false}
        noProjectHasTasks={noProjectsHasTasks}
        chromeless={chromeless}
        closable={closable}
        closeForm={broadcastMessageFrameClose}
        createProjectUrl={platformData?.createProjectUrl}
      />
    )
  }

  const textareaContainerClasses = classNames({
    'pds-flex': !company.wantsTimestampTimers,
  })

  const textareaClasses = classNames('entry-notes', 'pds-input', 'pds-mb-sm', {
    'has-value': !!notes,
  })

  const submitButtonLabel = submittingForm
    ? 'Saving…'
    : (!!startedTime && !!endedTime) || !!duration
      ? 'Save entry'
      : 'Start timer'

  let permalinkHost = null
  if (params.permalink) {
    try {
      const url = new URL(params.permalink)
      permalinkHost = url.hostname
    } catch {} // eslint-disable-line no-empty
  }

  const clearWarning = () => {
    setWarning(undefined)
  }

  const onNotesInput = ({ target }) => {
    if (platformData?.dayEntryNotesRequired) {
      target.setCustomValidity(
        target.value.trim() === '' ? notesValidationMessage : ''
      )
    }
    setNotes(target.value)
  }

  return (
    <div className="pds-row pds-m-0">
      <div className="pds-column pds-p-0">
        {header}
        {((params.permalink && !params.external_item_id) ||
          (params.external_item_id && !params.permalink)) && (
          <Alert
            className="pds-mt-md pds-mx-md"
            dismissable={false}
            variant="warning"
          >
            {permalinkHost || 'This application'} didn’t provide enough
            information for Harvest to associate this timer with an item on the
            page.
          </Alert>
        )}
        {warning && (
          <Alert
            className="pds-mt-md pds-mx-md"
            dismissable
            variant="warning"
            onDismiss={clearWarning}
          >
            <span dangerouslySetInnerHTML={{ __html: warning }} />
          </Alert>
        )}
        {lockedTimesheet && (
          <Alert
            className="pds-mt-md pds-mx-md"
            dismissable={false}
            variant="warning"
          >
            <span>
              You can’t track time to this week because your timesheet has
              already been approved.
            </span>
          </Alert>
        )}
        <form onSubmit={onSubmit} className={formClassName}>
          <div className="pds-flex pds-justify-between">
            <label className="pds-label" htmlFor="project_id">
              Project / Task
            </label>
            <CreateProjectLink
              externalGroupName={params.external_group_name}
              createProjectUrl={platformData?.createProjectUrl}
            />
          </div>
          <ProjectSelect
            closable={closable}
            projects={projects}
            onSelect={setProjectId}
            defaultProjectCode={defaultProjectCode}
            defaultProjectName={defaultProjectName}
            onChosenEvent={watchForHeightChanges}
            defaultProjectId={projectId || platformData?.suggestedProjectId}
          />
          <TaskSelect
            projectId={projectId}
            onSelect={setTaskId}
            onChosenEvent={watchForHeightChanges}
            defaultTaskId={taskId || platformData?.suggestedTaskId}
            tasks={tasks}
          />
          <div className={textareaContainerClasses}>
            <div className="notes-container pds-flex-fill">
              <textarea
                name="notes"
                ref={textareaRef}
                placeholder={
                  platformData?.dayEntryNotesRequired
                    ? 'Notes (required)'
                    : 'Notes (optional)'
                }
                className={textareaClasses}
                onInput={onNotesInput}
                onKeyDown={onKeyDown}
                defaultValue={notes}
                required={platformData?.dayEntryNotesRequired}
              />
              <span className="shift-enter-helper-text">
                <kbd>Shift+Return</kbd> for line break
              </span>
            </div>
            {company.wantsTimestampTimers && (
              <TimestampInputs
                ref={inputFormRef}
                onChange={(start, end) => {
                  setStartedTime(start)
                  setEndedTime(end)
                }}
              />
            )}
            {!company.wantsTimestampTimers && (
              <DurationInput ref={inputFormRef} onChange={setDuration} />
            )}
          </div>
          <div className="pds-mt-md pds-flex-list pds-justify-between pds-gap-sm">
            <div>
              <button
                aria-label={submitButtonLabel}
                disabled={
                  !isValid ||
                  submittingForm ||
                  loading ||
                  projectsLoading ||
                  timerLoading ||
                  platformDataLoading ||
                  lockedTimesheet
                }
                className="pds-button pds-button-primary"
                data-analytics-element-id="timesheet-day-entry-editor-start-timer-or-save-entry"
              >
                {submittingForm && <IconLoading className="pds-mr-xs" />}
                {submitButtonLabel}
              </button>
              {closable && (
                <Button
                  label="Cancel"
                  variant="cancel"
                  className="pds-ml-sm"
                  onClick={broadcastMessageFrameClose}
                  data-analytics-element-id="timesheet-day-entry-editor-cancel"
                />
              )}
              {showSaved && (
                <strong className="pds-inline-block pds-color-green pds-weight-bold pds-px-sm pds-mt-xs pds-mb-xs">
                  Saved!
                </strong>
              )}
            </div>
            <div>
              {(duration || (startedTime && endedTime)) && (
                <ChangeDate
                  spentDate={spentDate}
                  dateFormat={company.dateFormat}
                  onChange={setSpentDate}
                />
              )}
            </div>
          </div>
          {timerSummary}
          {params.service === 'app.asana.com' && userIsAdmin && (
            <AsanaProjectSyncPlug {...platformData?.integrations?.asana} />
          )}
        </form>
      </div>
    </div>
  )
}
